import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../utils/firebase';
import OrderModal from '../components/forms/OrderModal';
import Toast from '../components/common/Toast';
import DateRangeSelector from '../components/common/DateRangeSelector';
import PaginationControls from '../components/common/PaginationControls';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { collectionPath } from '../utils/firestoreCollections';
import { formatDateYYYYMMDD } from '../utils/dateHelpers';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { getRbacMatchNames } from '../utils/permissions';
import { monitorQuery, monitorParallelQueries } from '../utils/queryMonitoring';
import { search as typesenseSearch, isTypesenseAvailable } from '../utils/typesense';
import { useTableState } from '../hooks/useTableState';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import ExportButton from '../components/common/ExportButton';
import { exportData, checkExportSize } from '../utils/exportUtils';

const PAGE_SIZE = 50; // Increased from 25 for better UX
const INITIAL_LOAD = 100; // Hybrid: Load first 100 orders (orders load faster than customers)
const LOAD_MORE_BATCH = 100; // Load 100 more at a time
const MAX_ORDERS_FETCH = 50000; // Maximum orders to fetch from Firestore

const OrdersPage = ({ user }) => {
    const { currentUser } = useAuth();
    const { can, loading: permissionsLoading } = usePermissions(user);
    const [orders, setOrders] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true); // Start as true to show loading on mount
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editOrder, setEditOrder] = useState(null);
    const [toast, setToast] = useState(null);
    const fb = useFilterBar({
        search: '',
        statusFilter: [],
        range: '30',
        customStart: '',
        customEnd: '',
    });
    const { sortBy, sortDir, page: orderPage, setSort: setTableSort, setPage: setOrderPage } = useTableState({ sortBy: 'order_id', sortDir: 'desc', page: 1 });

    // Typesense state
    const [typesenseEnabled, setTypesenseEnabled] = useState(false);
    const [typesenseResults, setTypesenseResults] = useState([]);
    const [searchingTypesense, setSearchingTypesense] = useState(false);

    // Lazy loading state - use useRef to avoid stale closures
    const lastVisibleRef = useRef(null);
    const prevFetchDepsRef = useRef(null);
    const [hasMoreOrders, setHasMoreOrders] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Export state
    const [exporting, setExporting] = useState(false);

    const location = useLocation();

    // Check Typesense availability on mount
    useEffect(() => {
        const checkTypesense = async () => {
            const available = await isTypesenseAvailable();
            setTypesenseEnabled(available);
            if (available) {
                console.log('[OrdersPage] ✅ Typesense available for instant search');
            } else {
                console.log('[OrdersPage] ⚠️ Typesense unavailable, using Firestore only');
            }
        };
        checkTypesense();
    }, []);

    // Helper to get current user's name(s) for matching with order commissions
    // Synchronous useMemo avoids race conditions with async useEffect
    const userMatchNames = useMemo(() => {
        if (!user || permissionsLoading || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)) return [];
        return getRbacMatchNames(user);
    }, [user, permissionsLoading, can]);
    const userDisplayName = useMemo(() => {
        if (!user || permissionsLoading || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)) return '';
        return user.displayName || user.full_name || '';
    }, [user, permissionsLoading, can]);

    // Date range calculation
    const { start, end } = useMemo(() => {
        const now = new Date();
        let start, end;
        if (fb.applied.range === "custom") {
            // For custom range, require both dates
            if (!fb.applied.customStart || !fb.applied.customEnd) {
                return { start: null, end: null };
            }
            start = new Date(fb.applied.customStart);
            end = new Date(fb.applied.customEnd);
            
            // Validate custom date range (max 365 days)
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays > 365 || end < start) {
                return { start: null, end: null };
            }
        } else if (fb.applied.range === "ytd") {
            start = new Date(now.getFullYear(), 0, 1);
            end = now;
        } else {
            end = now;
            start = new Date(now.getTime() - Number(fb.applied.range) * 24 * 60 * 60 * 1000);
        }
        return { start, end };
    }, [fb.applied.range, fb.applied.customStart, fb.applied.customEnd]);

    // Fetch customers and users in parallel for better performance
    // BEST PRACTICE: Parallel queries minimize latency, monitoring tracks performance
    useEffect(() => {
        async function fetchInitialData() {
            if (!user) return;
            try {
                // Determine queries based on permissions
                const queries = [
                    {
                        name: 'fetch-customers-for-orders',
                        fn: () => getDocs(query(
                            collection(db, collectionPath('customers')),
                            orderBy('customerName', 'asc'),
                            limit(500)
                        ))
                    }
                ];
                
                if (can(FEATURE_PERMISSIONS.USER_VIEW_ALL)) {
                    queries.push({
                        name: 'fetch-users-for-orders',
                        fn: () => getDocs(query(
                            collection(db, collectionPath('users')),
                            orderBy('firstName', 'asc'),
                            limit(100)
                        ))
                    });
                } else {
                    queries.push({
                        name: 'fetch-current-user',
                        fn: () => getDocs(query(
                            collection(db, collectionPath('users')),
                            where('uid', '==', user.uid)
                        ))
                    });
                }
                
                const [customersSnap, usersSnap] = await monitorParallelQueries(queries);
                
                setCustomers(customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setUsers(usersSnap.docs.map(doc => ({ id: doc.id, uid: doc.id, ...doc.data() })));
            } catch (err) {
                console.error('Failed to load initial data:', err);
                // Don't set error here, as this is supplementary data
            }
        }
        fetchInitialData();
    }, [user, can]);

    // Fetch orders with hybrid lazy loading (initial 100, then 100 more at a time)
    // Use useCallback to prevent recreation on every render
    const fetchOrders = useCallback(async (loadMore = false) => {
        // Don't fetch if dates are invalid (null)
        if (!start || !end) {
            console.log('[OrdersPage] Invalid or missing date range - skipping fetch');
            setLoading(false);
            setHasLoaded(true);
            return;
        }
        
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError('');
        
        const startDateStr = formatDateYYYYMMDD(start);
        const endDateStr = formatDateYYYYMMDD(end);
        
        const needsRbac = !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && userMatchNames.length > 0;
        const targetCount = loadMore ? LOAD_MORE_BATCH : INITIAL_LOAD;
        const matchSet = needsRbac ? new Set(userMatchNames) : null;
        
        // For RBAC users, loop to accumulate enough matching orders
        const RAW_BATCH = 500; // Fetch larger raw batches for RBAC users to reduce round trips
        const batchSize = needsRbac ? RAW_BATCH : targetCount;
        let accumulated = [];
        let cursor = loadMore ? lastVisibleRef.current : null;
        let exhausted = false;
        
        if (loadMore && !cursor) {
            console.warn('[OrdersPage] ⚠️ Cannot load more - lastVisible is null');
            setLoadingMore(false);
            return;
        }
        
        try {
            while (accumulated.length < targetCount && !exhausted) {
                let qArr = [
                    where('date_internal', '>=', startDateStr),
                    where('date_internal', '<=', endDateStr),
                    orderBy('date_internal', 'desc')
                ];
                
                if (cursor) {
                    qArr.push(startAfter(cursor));
                }
                qArr.push(limit(batchSize));
                
                const ordersQuery = query(collection(db, collectionPath('orders')), ...qArr);
                const snap = await monitorQuery(
                    loadMore ? 'load-more-orders' : 'fetch-orders',
                    () => getDocs(ordersQuery)
                );
                
                const fetchedOrders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                
                if (needsRbac) {
                    const matched = fetchedOrders.filter(order => {
                        const hasMatch = Array.isArray(order.commissions_displayNames)
                            ? order.commissions_displayNames.some(name => name && matchSet.has(name.toLowerCase().trim()))
                            : Array.isArray(order.commissions)
                            ? order.commissions.some(c => c.displayName && matchSet.has(c.displayName.toLowerCase().trim()))
                            : false;
                        return hasMatch;
                    });
                    accumulated.push(...matched);
                } else {
                    accumulated.push(...fetchedOrders);
                }
                
                // Update cursor for next iteration
                if (snap.docs.length > 0) {
                    cursor = snap.docs[snap.docs.length - 1];
                }
                
                // Stop if Firestore returned fewer docs than requested (no more data)
                if (snap.docs.length < batchSize) {
                    exhausted = true;
                }
                
                // Safety: don't loop forever for admins (they don't need RBAC loop anyway)
                if (!needsRbac) break;
            }
            
            console.log(`[OrdersPage] 🔒 RBAC accumulated ${accumulated.length} orders (target: ${targetCount}, exhausted: ${exhausted})`);
            
            // Update state
            if (loadMore) {
                setOrders(prev => {
                    const existingIds = new Set(prev.map(o => o.id));
                    const newRows = accumulated.filter(o => !existingIds.has(o.id));
                    const combined = [...prev, ...newRows];
                    console.log(`[OrdersPage] 📦 Loaded ${newRows.length} more orders (total: ${combined.length})`);
                    return combined;
                });
            } else {
                setOrders(accumulated);
                console.log(`[OrdersPage] 📦 Initial load: ${accumulated.length} orders`);
            }
            
            // Track cursor for next load
            if (cursor) {
                lastVisibleRef.current = cursor;
            }
            
            setHasMoreOrders(!exhausted);
            console.log(`[OrdersPage] 📊 Loaded ${accumulated.length} orders, hasMore: ${!exhausted}`);
            
        } catch (err) {
            setError('Failed to load orders');
            console.error('Error fetching orders:', err);
        }
        
        if (loadMore) {
            setLoadingMore(false);
        } else {
            setLoading(false);
            setHasLoaded(true);
        }
    }, [start, end, userDisplayName, can]);

    // Typesense search effect
    useEffect(() => {
        if (!typesenseEnabled) {
            setTypesenseResults([]);
            setSearchingTypesense(false);
            return;
        }

        // Wait for permissions to load before searching (affects RBAC filter)
        if (permissionsLoading) return;

        // Only use Typesense if there's a search query OR active filters
        const hasFilters = (fb.applied.statusFilter && fb.applied.statusFilter.length > 0) || (start && end);
        if (!fb.applied.search && !hasFilters) {
            setTypesenseResults([]);
            setSearchingTypesense(false);
            return;
        }

        const searchTypesense = async () => {
            setSearchingTypesense(true);
            const startTime = performance.now();
            
            try {
                const searchParams = {
                    q: fb.applied.search || '*', // Use '*' to match all when filtering without search
                    query_by: 'order_number,customer_name,customer_po',
                    per_page: 250
                };
                
                // Add server-side filtering
                const filterBy = [];
                
                // RBAC: Non-admin users can only see orders they're assigned to
                if (!can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && userMatchNames.length > 0) {
                    // Build filter with original-case names for Typesense
                    const rbacNames = [];
                    if (user?.displayName) rbacNames.push(user.displayName.trim());
                    if (user?.full_name && user.full_name.trim().toLowerCase() !== (user.displayName || '').trim().toLowerCase()) {
                        rbacNames.push(user.full_name.trim());
                    }
                    if (rbacNames.length > 0) {
                        filterBy.push(`commissions_displayNames:=[${rbacNames.join(',')}]`);
                    }
                }
                
                // Status filter
                if (fb.applied.statusFilter && fb.applied.statusFilter.length > 0) {
                    filterBy.push(`status:[${fb.applied.statusFilter.join(',')}]`);
                }
                
                // Date range filter using creation_time (int64 Unix timestamp)
                // This is required because date_internal is a string and doesn't support >= operators
                if (start && end) {
                    const startTimestamp = Math.floor(start.getTime() / 1000); // Convert to Unix seconds
                    const endTimestamp = Math.floor(end.getTime() / 1000) + 86400; // +1 day to include end date
                    filterBy.push(`creation_time:>=${startTimestamp} && creation_time:<=${endTimestamp}`);
                }
                
                if (filterBy.length > 0) {
                    searchParams.filter_by = filterBy.join(' && ');
                }
                
                const response = await typesenseSearch('orders', searchParams);
                const duration = performance.now() - startTime;
                
                // Use Typesense results directly - no Firestore fetch needed!
                const results = (response.hits || []).map(hit => ({
                    id: hit.document.id,
                    ...hit.document
                }));
                
                setTypesenseResults(results);
                
                const filterMsg = filterBy.length > 0 ? ` (with ${filterBy.length} server filters)` : '';
                console.log(`[OrdersPage] 🚀 Typesense found ${results.length} orders in ${Math.round(duration)}ms${filterMsg}`);
                
            } catch (error) {
                console.error('[OrdersPage] Typesense search failed:', error);
                setTypesenseResults([]);
            } finally {
                setSearchingTypesense(false);
            }
        };
        
        searchTypesense();
    }, [typesenseEnabled, fb.applied.search, fb.applied.statusFilter, start, end, can, userDisplayName, permissionsLoading]);

    // When filters change, reset to page 1 and fetch
    useEffect(() => {
        // Wait for permissions to load before fetching to avoid incorrect RBAC decisions
        if (!user || permissionsLoading) return;
        if (!can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && !userDisplayName) return;
        
        // Skip Firestore fetch if searching (Typesense handles it)
        const searchLower = fb.applied.search.toLowerCase().trim();
        if (searchLower && typesenseEnabled) {
            console.log('[OrdersPage] Skipping Firestore - Typesense will handle search + filters');
            return;
        }
        
        // Reset lazy loading when date range changes
        lastVisibleRef.current = null;
        setHasMoreOrders(true);
        const depsKey = `${start}-${end}-${fb.applied.range}-${fb.applied.search}`;
        if (prevFetchDepsRef.current !== null && prevFetchDepsRef.current !== depsKey) {
            setOrderPage(1);
        }
        prevFetchDepsRef.current = depsKey;
        fetchOrders(false);
    }, [start, end, fb.applied.range, user, userDisplayName, fb.applied.search, typesenseEnabled, fetchOrders, can, permissionsLoading]);

    // Pagination controls for in-memory pagination
    // Note: These reference calculatedTotalPages which is computed later
    const handleNextPage = () => {
        // Use sortedOrders.length to calculate max pages dynamically
        const maxPages = Math.max(1, Math.ceil(sortedOrders.length / PAGE_SIZE));
        if (orderPage < maxPages) {
            setOrderPage(p => p + 1);
        }
    };
    const handlePrevPage = () => {
        if (orderPage > 1) {
            setOrderPage(p => p - 1);
        }
    };
    const handlePageChange = (newPage) => {
        setOrderPage(newPage);
    };

    // Load more orders function
    const loadMoreOrders = async () => {
        if (!hasMoreOrders || loadingMore) return;
        await fetchOrders(true); // Pass loadMore = true
    };

    // Export all orders in the current date range (bypasses "Load More" pagination).
    // - Enforces max 365-day range (matches the UI custom-range validation)
    // - Uses the indexed date_internal query to fetch the full range in one pass
    // - Applies the same RBAC + status + search filters used by the visible list
    // - Delegates to shared exportUtils (CSV / XLSX / PDF) with size guardrails
    const MAX_EXPORT_ROWS = 20000; // Matches exportUtils CSV BACKGROUND cap
    const EXPORT_PAGE_SIZE = 5000; // Firestore hard-caps limit() at 10000; page below that
    const handleExport = async (format) => {
        if (exporting) return;
        if (!start || !end) {
            setToast({ message: 'Please select a valid date range before exporting.', type: 'error' });
            return;
        }

        // Enforce 365-day maximum (independent of custom-range UI validation)
        const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
        if (diffDays > 365) {
            setToast({ message: 'Export is limited to 365 days. Please narrow the date range.', type: 'error' });
            return;
        }

        setExporting(true);
        try {
            const startDateStr = formatDateYYYYMMDD(start);
            const endDateStr = formatDateYYYYMMDD(end);
            const needsRbac = !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && userMatchNames.length > 0;
            const matchSet = needsRbac ? new Set(userMatchNames) : null;

            // Paginate the indexed date_internal query with a cursor.
            // Firestore rejects limit() values above 10000, so we page in EXPORT_PAGE_SIZE chunks
            // up to MAX_EXPORT_ROWS raw rows for the full range.
            const rawRows = [];
            let cursor = null;
            let exhausted = false;
            while (rawRows.length < MAX_EXPORT_ROWS && !exhausted) {
                const remaining = MAX_EXPORT_ROWS - rawRows.length;
                const pageLimit = Math.min(EXPORT_PAGE_SIZE, remaining);
                const qArr = [
                    where('date_internal', '>=', startDateStr),
                    where('date_internal', '<=', endDateStr),
                    orderBy('date_internal', 'desc')
                ];
                if (cursor) qArr.push(startAfter(cursor));
                qArr.push(limit(pageLimit));

                const ordersQuery = query(collection(db, collectionPath('orders')), ...qArr);
                const snap = await monitorQuery('export-orders', () => getDocs(ordersQuery));
                rawRows.push(...snap.docs.map(d => ({ id: d.id, ...d.data() })));

                if (snap.docs.length < pageLimit) {
                    exhausted = true;
                } else {
                    cursor = snap.docs[snap.docs.length - 1];
                }
            }
            const rawCount = rawRows.length;
            let rows = rawRows;

            // Apply RBAC filter
            if (needsRbac) {
                rows = rows.filter(order => (
                    Array.isArray(order.commissions_displayNames)
                        ? order.commissions_displayNames.some(n => n && matchSet.has(n.toLowerCase().trim()))
                        : Array.isArray(order.commissions)
                        ? order.commissions.some(c => c.displayName && matchSet.has(c.displayName.toLowerCase().trim()))
                        : false
                ));
            }

            // Apply the same status filter as the visible list
            if (fb.applied.statusFilter && fb.applied.statusFilter.length > 0) {
                const statusSet = new Set(fb.applied.statusFilter);
                rows = rows.filter(o => statusSet.has(o.status));
            }

            // Apply the same search filter (order #, customer name, PO #) as the visible list
            const searchLower = (fb.applied.search || '').toLowerCase().trim();
            if (searchLower) {
                rows = rows.filter(order => {
                    const orderIdStr = (order.order_id || '').toString().toLowerCase();
                    const poStr = (order.customer_po || '').toString().toLowerCase();
                    if (orderIdStr.includes(searchLower) || poStr.includes(searchLower)) return true;
                    const customer = customersById.get(order.customerId);
                    const nameStr = (order.customer_name || customer?.customerName || customer?.name || '').toLowerCase();
                    return nameStr.includes(searchLower);
                });
            }

            if (rows.length === 0) {
                setToast({ message: 'No orders to export for the current filters.', type: 'info' });
                setExporting(false);
                return;
            }

            // Size guardrails from shared exportUtils
            const sizeCheck = checkExportSize(format, rows.length);
            if (!sizeCheck.allowed) {
                setToast({ message: sizeCheck.message.replace(/\n+/g, ' '), type: 'error' });
                setExporting(false);
                return;
            }
            if (sizeCheck.level === 'warning' || sizeCheck.level === 'background') {
                // eslint-disable-next-line no-alert
                const proceed = window.confirm(sizeCheck.message);
                if (!proceed) {
                    setExporting(false);
                    return;
                }
            }

            // Row mapper - matches the columns shown on the page
            const headers = ['Order #', 'Order Date', 'Customer', 'PO #', 'Status', 'Qty Ordered', 'Qty Shipped', 'Total', 'Sales Rep'];
            const rowMapper = (order) => {
                let qtyOrdered = typeof order.qty_ordered === 'number' ? order.qty_ordered : 0;
                let qtyShipped = typeof order.qty_shipped === 'number' ? order.qty_shipped : 0;
                if (qtyOrdered === 0 && qtyShipped === 0 && Array.isArray(order.order_items)) {
                    order.order_items.forEach(item => {
                        qtyOrdered += parseFloat(item.qty) || 0;
                        qtyShipped += parseFloat(item.qty_shipped) || 0;
                    });
                }
                const customer = customersById.get(order.customerId);
                const customerName = order.customer_name || customer?.customerName || customer?.name || 'Unknown Customer';
                const salesRep = Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0
                    ? order.commissions_displayNames.join(', ')
                    : Array.isArray(order.commissions) && order.commissions.length > 0
                    ? order.commissions.map(c => c.displayName || c.name).filter(Boolean).join(', ')
                    : 'Unassigned';
                return [
                    order.order_id || '',
                    order.date_internal || order.date || '',
                    customerName,
                    order.customer_po || '',
                    order.fulfillment_status || order.status || '',
                    qtyOrdered,
                    qtyShipped,
                    Number(order.amount) || 0,
                    salesRep
                ];
            };

            const filename = `orders_${startDateStr}_to_${endDateStr}`;
            exportData({
                filename,
                format,
                headers,
                data: rows,
                rowMapper,
                options: {
                    title: `Orders ${startDateStr} to ${endDateStr}`,
                    orientation: 'landscape'
                }
            });

            const truncatedNote = rawCount >= MAX_EXPORT_ROWS
                ? ` (raw fetch capped at ${MAX_EXPORT_ROWS.toLocaleString()} rows - narrow the date range for a complete export)`
                : '';
            setToast({
                message: `Exported ${rows.length.toLocaleString()} order${rows.length === 1 ? '' : 's'} to ${format.toUpperCase()}${truncatedNote}`,
                type: 'success'
            });
        } catch (err) {
            console.error('[OrdersPage] Export failed:', err);
            setToast({ message: `Export failed: ${err.message || 'Unknown error'}`, type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    // Create a Map for O(1) customer lookups instead of O(n) find operations
    const customersById = useMemo(() => {
        const map = new Map();
        customers.forEach(c => map.set(c.id, c));
        return map;
    }, [customers]);

    const usersById = useMemo(() => {
        const map = new Map();
        users.forEach(u => map.set(u.id, u));
        return map;
    }, [users]);

    // Determine which data source to use: Typesense results or Firestore orders
    const displayOrders = useMemo(() => {
        const searchLower = fb.applied.search.toLowerCase().trim();
        
        // If searching, use Typesense results
        if (searchLower && typesenseEnabled && typesenseResults.length > 0) {
            console.log(`[OrdersPage] Using ${typesenseResults.length} Typesense results`);
            return typesenseResults;
        }
        
        // Otherwise use Firestore orders
        return orders;
    }, [fb.applied.search, typesenseEnabled, typesenseResults, orders]);

    // Filter and sort orders in-memory (after server-side filtering)
    const filteredOrders = useMemo(() => {
        // Early return if no search or status filter
        const hasSearchFilter = fb.applied.search.trim().length > 0;
        const hasStatusFilter = fb.applied.statusFilter.length > 0;
        const needsUserFilter = currentUser && !can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && userMatchNames.length > 0;
        const matchSet = needsUserFilter ? new Set(userMatchNames) : null;

        return displayOrders.filter(order => {
            // For non-admins, filter by matching displayName/full_name with commissions array
            if (needsUserFilter) {
                const hasMatchingCommission = Array.isArray(order.commissions_displayNames)
                    ? order.commissions_displayNames.some(name => 
                        name && matchSet.has(name.toLowerCase().trim())
                      )
                    : Array.isArray(order.commissions)
                    ? order.commissions.some(c => 
                        c.displayName && matchSet.has(c.displayName.toLowerCase().trim())
                      )
                    : false;
                
                if (!hasMatchingCommission) return false;
            }

            // Status filter
            if (hasStatusFilter && !fb.applied.statusFilter.includes(order.status)) {
                return false;
            }

            // Search filter - only build search strings if searching
            if (hasSearchFilter) {
                const searchLower = fb.applied.search.toLowerCase();
                const order_idStr = (order.order_id || '').toString().toLowerCase();
                const customer_poStr = (order.customer_po || '').toString().toLowerCase();
                
                // Quick checks first (most common search terms)
                if (order_idStr.includes(searchLower) || customer_poStr.includes(searchLower)) {
                    return true;
                }

                // Customer name check - use Map for O(1) lookup
                const customer = customersById.get(order.customerId);
                const customerNameStr = (order.customer_name || customer?.customerName || customer?.name || '').toLowerCase();
                if (customerNameStr.includes(searchLower)) {
                    return true;
                }

                // Date search - only build complex date strings if needed
                if (order.date_internal) {
                    let orderDate = null;
                    if (typeof order.date_internal.toDate === 'function') {
                        orderDate = order.date_internal.toDate();
                    } else if (/^\d{4}-\d{2}-\d{2}$/.test(order.date_internal)) {
                        const [y, m, d] = order.date_internal.split('-').map(Number);
                        orderDate = new Date(y, m - 1, d);
                    } else {
                        orderDate = new Date(order.date_internal);
                    }

                    if (orderDate) {
                        const month = orderDate.getMonth() + 1;
                        const day = orderDate.getDate();
                        const year = orderDate.getFullYear();
                        const pad = n => n.toString().padStart(2, '0');

                        const orderDateStr = [
                            orderDate.toLocaleDateString().toLowerCase(),
                            year.toString(),
                            month.toString(),
                            orderDate.toLocaleString('default', { month: 'short' }).toLowerCase(),
                            orderDate.toLocaleString('default', { month: 'long' }).toLowerCase(),
                            day.toString(),
                            `${month}/${day}/${year}`,
                            `${pad(month)}/${pad(day)}/${year}`,
                            `${month}-${day}-${year}`,
                            `${pad(month)}-${pad(day)}-${year}`,
                            `${year}/${pad(month)}/${pad(day)}`,
                            `${year}-${pad(month)}-${pad(day)}`
                        ].join(' ').toLowerCase();

                        if (orderDateStr.includes(searchLower)) {
                            return true;
                        }
                    }
                }

                return false; // No match found
            }

            return true; // No filters applied, include order
        });
    }, [displayOrders, currentUser, userDisplayName, fb.applied.search, fb.applied.statusFilter, customersById, can]);

    const sortedOrders = useMemo(() => {
        // Don't create a copy if no sorting needed
        if (!sortBy) return filteredOrders;

        const sorted = [...filteredOrders];
        sorted.sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];
            
            // Special handling for order_id - sort numerically
            if (sortBy === 'order_id') {
                const aNum = parseInt(a.order_id) || 0;
                const bNum = parseInt(b.order_id) || 0;
                return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
            }
            
            // For customer, sort by customer_name or customer name (use Map for O(1) lookup)
            if (sortBy === 'customer') {
                const customerA = customersById.get(a.customerId);
                const customerB = customersById.get(b.customerId);
                aValue = a.customer_name || customerA?.customerName || customerA?.name || '';
                bValue = b.customer_name || customerB?.customerName || customerB?.name || '';
            }
            
            // For customer_po (PO#), sort as string
            if (sortBy === 'customer_po') {
                aValue = (a.customer_po || '').toString();
                bValue = (b.customer_po || '').toString();
            }
            
            // For status (uses fulfillment_status which is what's displayed)
            if (sortBy === 'status') {
                aValue = (a.fulfillment_status || '').toString();
                bValue = (b.fulfillment_status || '').toString();
            }
            
            // For qty_ordered - sort numerically
            else if (sortBy === 'qty_ordered') {
                aValue = parseFloat(a.qty_ordered) || 0;
                bValue = parseFloat(b.qty_ordered) || 0;
            }
            
            // For qty_shipped - sort numerically
            else if (sortBy === 'qty_shipped') {
                aValue = parseFloat(a.qty_shipped) || 0;
                bValue = parseFloat(b.qty_shipped) || 0;
                return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // For total (amount) - sort numerically
            if (sortBy === 'total') {
                aValue = parseFloat(a.amount) || 0;
                bValue = parseFloat(b.amount) || 0;
                return sortDir === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // For salesRep, use commissions_displayNames array
            if (sortBy === 'salesRep') {
                // Get first sales rep from commissions array
                aValue = (a.commissions_displayNames && a.commissions_displayNames.length > 0)
                    ? a.commissions_displayNames[0]
                    : '';
                bValue = (b.commissions_displayNames && b.commissions_displayNames.length > 0)
                    ? b.commissions_displayNames[0]
                    : '';
                return sortDir === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            
            // Fallback for any other fields
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortDir === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            return sortDir === 'asc'
                ? (aValue ?? 0) - (bValue ?? 0)
                : (bValue ?? 0) - (aValue ?? 0);
        });
        return sorted;
    }, [filteredOrders, customersById, usersById, sortBy, sortDir]);

    // Determine pagination mode
    const isSearchMode = typesenseEnabled && fb.applied.search.trim();
    const isFilteredMode = fb.applied.statusFilter.length > 0;
    const usePagination = isSearchMode || isFilteredMode;

    // Calculate total pages based on current data (important for Typesense results)
    const calculatedTotalPages = useMemo(() => {
        return Math.max(1, Math.ceil(sortedOrders.length / PAGE_SIZE));
    }, [sortedOrders.length]);

    // In-memory pagination
    const paginatedOrders = usePagination
        ? sortedOrders.slice((orderPage - 1) * PAGE_SIZE, orderPage * PAGE_SIZE)
        : sortedOrders;

    useEffect(() => {
        setShowModal(false);
        setEditOrder(null);
    }, [location.pathname]);

    const handleSort = (field) => {
        const newDir = sortBy === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setTableSort(field, newDir);
    };

    // Show message when custom range selected but dates not entered
    const showCustomRangePrompt = fb.applied.range === 'custom' && (!fb.applied.customStart || !fb.applied.customEnd);

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            {showModal && (
                <OrderModal
                    user={user}
                    initial={editOrder}
                    customers={customers}
                    onClose={() => { setShowModal(false); setEditOrder(null); }}
                    onSaved={(msg) => { setShowModal(false); setEditOrder(null); setToast({ message: msg, type: 'success' }); }}
                    users={users}
                />
            )}

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Orders</h2>
                <ExportButton
                    onExport={handleExport}
                    disabled={exporting || loading || !start || !end}
                    tooltipText="Export all orders in the selected date range (max 365 days). Bypasses 'Load More' pagination."
                    label={exporting ? 'Exporting…' : 'Export'}
                />
            </div>

            {/* Custom range prompt */}
            {showCustomRangePrompt && (
                <div className="mb-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-center">
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-yellow-800">
                        Please select start and end dates to view custom range data
                    </span>
                </div>
            )}

            {/* Date Range Selector */}
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-4 space-y-2 md:space-y-0">
                <div className="flex items-center w-full md:w-auto">
                    <span className="mr-2 font-medium text-gray-700">Search:</span>
                    <input
                        type="text"
                        placeholder="Search by order #, customer name, or PO #..."
                        className={`border rounded px-3 py-2 w-full md:w-[32rem] transition-colors duration-150 ${
                            fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                        }`}
                        value={fb.draft.search || ''}
                        onChange={e => fb.setDraft('search', e.target.value)}
                        onKeyDown={fb.applyOnEnter}
                    />
                </div>
                <DateRangeSelector
                    range={fb.draft.range}
                    setRange={v => fb.setDraft('range', v)}
                    customStart={fb.draft.customStart}
                    setCustomStart={v => fb.setDraft('customStart', v)}
                    customEnd={fb.draft.customEnd}
                    setCustomEnd={v => fb.setDraft('customEnd', v)}
                    defaultRange="30"
                    hideClear
                />
                <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
            </div>

            {error && <p className="text-red-500 mb-4">{error}</p>}

            {!showCustomRangePrompt && (
                <div className="bg-white rounded-lg shadow">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('order_id')}>
                                        Order # {sortBy === 'order_id' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('date')}>
                                        Order Date {sortBy === 'date' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('customer')}>
                                        Customer {sortBy === 'customer' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('customer_po')}>
                                        PO # {sortBy === 'customer_po' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('status')}>
                                        Status {sortBy === 'status' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('qty_ordered')}>
                                        Qty Ordered {sortBy === 'qty_ordered' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('qty_shipped')}>
                                        Qty Shipped {sortBy === 'qty_shipped' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('total')}>
                                        Total {sortBy === 'total' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('salesRep')}>
                                        Sales Rep {sortBy === 'salesRep' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {loading ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-8 text-gray-500">
                                            Loading orders...
                                        </td>
                                    </tr>
                                ) : paginatedOrders.length > 0 ? (
                                    paginatedOrders.map(order => {
                                    // Use Map for O(1) lookup instead of find
                                    const customer = customersById.get(order.customerId);
                                    const salesRep = usersById.get(order.uid);
                                    
                                    // Use pre-computed quantities from Typesense (if available), otherwise calculate
                                    let qtyOrdered = typeof order.qty_ordered === 'number' ? order.qty_ordered : 0;
                                    let qtyShipped = typeof order.qty_shipped === 'number' ? order.qty_shipped : 0;
                                    
                                    // Fallback to calculation if not pre-computed (for Firestore-loaded orders)
                                    if (qtyOrdered === 0 && qtyShipped === 0 && Array.isArray(order.order_items)) {
                                        order.order_items.forEach(item => {
                                            qtyOrdered += parseFloat(item.qty) || 0;
                                            qtyShipped += parseFloat(item.qty_shipped) || 0;
                                        });
                                    }
                                    return (
                                        <tr key={order.id} className="text-gray-700">
                                            <td className="px-4 py-3 text-sm font-medium">
                                                <Link
                                                    to={`/order/${order.id}`}
                                                    className="text-indigo-600 hover:underline"
                                                >
                                                    {order.order_id}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{order.date || order.date_internal}</td>
                                            <td className="px-4 py-3 text-sm">
                                                {order.customer_name || customer?.name || 'Unknown Customer'}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{order.customer_po || '-'}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    order.fulfillment_status === 'Shipped' ? 'bg-green-100 text-green-700' :
                                                    order.fulfillment_status === 'Cancelled' || order.fulfillment_status === 'Returned' ? 'bg-red-100 text-red-700' :
                                                    order.fulfillment_status === 'Complete' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                    {order.fulfillment_status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm">{qtyOrdered}</td>
                                            <td className="px-4 py-3 text-sm">{qtyShipped}</td>
                                            <td className="px-4 py-3 text-sm font-medium">${(order.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            <td className="px-4 py-3 text-sm">
                                                {Array.isArray(order.commissions_displayNames) && order.commissions_displayNames.length > 0
                                                    ? order.commissions_displayNames.join(', ')
                                                    : Array.isArray(order.commissions) && order.commissions.length > 0
                                                    ? order.commissions.map(c => c.displayName || c.name).filter(Boolean).join(', ')
                                                    : 'Unassigned'}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (hasLoaded && !loading && paginatedOrders.length === 0) ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-8 text-gray-500">
                                        {error || 'No orders found'}
                                    </td>
                                </tr>
                            ) : null}
                        </tbody>
                    </table>
                    {/* Pagination controls - Only show for search/filter modes */}
                    {usePagination && (
                        <PaginationControls currentPage={orderPage} totalPages={calculatedTotalPages} onPageChange={handlePageChange} totalItems={sortedOrders.length} pageSize={PAGE_SIZE} itemLabel="orders" />
                    )}
                    {/* Load More Button - Only show for browse mode (no search/filter) */}
                    {!usePagination && hasMoreOrders && (
                        <div className="flex justify-center py-4 border-t border-gray-200">
                            <button
                                onClick={loadMoreOrders}
                                disabled={loadingMore}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {loadingMore ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                        Load More Orders
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            )}
        </main>
    );
};

export default OrdersPage;
